/**
 * 厚み 0 のプレーンを既存の紙面に重ねたとき、同じ中面だと拾えない。
 * 輪郭は動かさず、重なった側だけ紙面より厚い厚みを付ける。
 */

import { polygonsOverlap } from './holes';
import {
  planeBasis,
  planeExtrudeDistance,
  vec,
  ZERO_PLANE_THICKNESS,
} from './geometry';
import type { PlaneGeometry, Vec3 } from './types';
import { computePolygonNormal } from './validation';

/** 紙面（±ZERO_PLANE_THICKNESS/2）の外側に面が出る最小の実厚み。 */
export const OVERLAP_PLANE_THICKNESS = ZERO_PLANE_THICKNESS * 2;

const COPLANAR_NORMAL_EPS = 1e-4;
const COPLANAR_DISTANCE_EPS = 1e-3;

export function isPaperThinPlane(geometry: PlaneGeometry): boolean {
  return geometry.thickness <= 1e-9 && planeExtrudeDistance(geometry) === 0;
}

function projectToPlane2D(
  points: Vec3[],
  origin: Vec3,
  tangent: Vec3,
  bitangent: Vec3
): { x: number; y: number }[] {
  return points.map((point) => {
    const d = vec.sub(point, origin);
    return { x: vec.dot(d, tangent), y: vec.dot(d, bitangent) };
  });
}

/** 同一平面上で輪郭が重なる（辺の接触も含む）。法線の向きは問わない。 */
export function fillsCoplanarOverlap(
  points: Vec3[],
  normal: Vec3,
  existing: PlaneGeometry
): boolean {
  if (points.length < 3 || existing.points.length < 3) return false;
  const existingNormal = existing.normal ?? computePolygonNormal(existing.points);
  if (!existingNormal) return false;
  const a = vec.normalize(normal);
  const b = vec.normalize(existingNormal);
  if (Math.abs(Math.abs(vec.dot(a, b)) - 1) > COPLANAR_NORMAL_EPS) return false;

  const origin = existing.points[0];
  for (const point of points) {
    if (Math.abs(vec.dot(vec.sub(point, origin), b)) > COPLANAR_DISTANCE_EPS) return false;
  }

  const { tangent, bitangent } = planeBasis(b);
  return polygonsOverlap(
    projectToPlane2D(points, origin, tangent, bitangent),
    projectToPlane2D(existing.points, origin, tangent, bitangent)
  );
}

/**
 * 厚み 0 を紙面に重ねるときだけ OVERLAP_PLANE_THICKNESS にする。
 * 指定厚みがある、重なっていない、相手が押し出し済み、は元の厚みのまま。
 */
export function resolveOverlappingPlaneThickness(
  points: Vec3[],
  normal: Vec3,
  thickness: number,
  existing: readonly PlaneGeometry[]
): number {
  if (thickness > 1e-9) return thickness;
  for (const plane of existing) {
    if (!isPaperThinPlane(plane)) continue;
    if (fillsCoplanarOverlap(points, normal, plane)) return OVERLAP_PLANE_THICKNESS;
  }
  return thickness;
}
